import classNames from 'classnames';
import { AnimatePresence, domAnimation, LazyMotion, m } from 'framer-motion';
import DOMPurify from 'dompurify';
import { throttle } from 'underscore';

import { useIsNewModuleGrouping } from 'hooks/session.hooks';
import React, { CSSProperties, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ContentEditable, { ContentEditableEvent } from 'react-contenteditable';
import { useSessionUuidParamAdmin } from 'utils/use-session-uuid-param';

import { useTypedSelector } from '../../../store/reducers/use-typed-selector';
import { TextOverrides, } from '../../../types/working-model';
import { parseColor } from '../../../utils/document-head';
import { OptionalComponent } from '../../../utils/optional-component';
import { useRefCallback } from '../../../utils/use-ref-callback';
import EditSectionColorsModal, { EContentColorOverrideNames } from '../edit-section-colors/edit-section-colors-modal';
import Icon, { COLORS, ICONS } from '../icon';
import { useGetColorOptionsFromColor } from 'utils/colors/use-get-color-options-from-color';
import {
	EPaletteModes,
	ColorKeys,
	ColorOptions,
	TColorVariantKeys,
} from 'types/theme-packs';
import { ThemeContext } from 'components/live-event/theme-context';
import { selectEditorNavV3 } from '@/store/selectors/feature-flags';
import stripNewLinesFromString from '@/utils/strip-new-lines-from-string';
import { isRichTextEmpty } from '@/utils/is-rich-text-empty';

import './editable-text.scss';

type MultiColorType = TColorVariantKeys | ColorKeys;

const useStartingColor = (color: MultiColorType | undefined, defaultColor?: MultiColorType): MultiColorType | undefined => {
	const sessionUuid = useSessionUuidParamAdmin();
	const isModuleGroupingV2 = useIsNewModuleGrouping();

	const [theme] = useContext(ThemeContext);
	const isDarkMode = theme === EPaletteModes.Dark;

	return useMemo(() => {
		if (color) {
			return color;
		}

		if (sessionUuid && isModuleGroupingV2) {
			if (isDarkMode) {
				return ColorKeys.darkModeColor;
			} else {
				return ColorKeys.lightModeColor;
			}
		}

		return defaultColor;
	}, [color, defaultColor, isDarkMode, isModuleGroupingV2, sessionUuid]);
};

const useCurrentColor = (
	startingColor: MultiColorType | undefined,
	colorPalette: ColorOptions | undefined
): [string, number] => {
	return useMemo(() => {
		if (startingColor) {
			if (startingColor === ColorKeys.lightModeColor) {
				return ['#081543', 1];
			} else if (startingColor === ColorKeys.darkModeColor) {
				return ['#FFFFFF', 1];
			}

			const color = colorPalette?.[startingColor];
			if (!Array.isArray(color)) return ['#444', 1];

			return color;
		}

		return ['#444', 1];
	}, [colorPalette, startingColor]);
};


interface Props {
	value: string;
	/** handles text changes (this only runs on blur) */
	onChange: (value: string) => void;
	/** handles text changes (this runs on text change) */
	onTextChange?: (value: string) => void;
	style?: React.CSSProperties;
	className?: string;
	elementClass?: string;

	tagType?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span";//creates the specified tag and removes markdown, more tag types can be added

	elementName?: EContentColorOverrideNames; //name of the element being edited (description/title), used in the color editor modal

	//Your onOverrideChange func should update the styling_override attribute of the module you're editing
	//and include pathing to the specific elemnt you're editing
	//e.g.
	// {
	// 	...module,
	// 	styling_overrides: {
	// 		...module.styling_overrides,
	// 		[your element e.g "title"]: override
	// 	}
	// }
	onOverrideChange?: (override: TextOverrides) => void;
	// that path is also what you would pass in as the initial textOverrides
	colorOverride?: string;
	hideColors?: boolean;

	//include these defaults if you want to give the user the option of editing an override without setting one before hand. 
	//The default should represet what the template would be (title is "h1"/"h2" and "headingTextcolor", most things are left aligned etc.)
	defaultColor?: MultiColorType;
	disabled?: boolean;
	maxCharacters?: number;
	displayCountAtXCharacter?: number;
	onEditableClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
	onEditableContainerClick?: React.MouseEventHandler<HTMLDivElement> | undefined;

	onFocus?: () => void;
	onBlur?: () => void;
	placeholder?: string;
	hidePencil?: boolean;
	isMultiLine?: boolean;
	plainTextOnly?: boolean;
	sanitize?: boolean;
	/** render the hover "edit" button in a fixed-position portal so it isn't clipped by tight/overflow-hidden containers */
	floatEditButton?: boolean;
	emptyState?: {
		showOutline?: boolean;
		wrapperStyles?: CSSProperties;
		contentStyles?: CSSProperties;
		frameStyles?: CSSProperties;
	}
}

function convertFromHtml(html: string): string {
	const tempCont = document.createElement('div');
	tempCont.innerHTML = html;

	return tempCont.innerText;
}

const FLOAT_EDIT_BUTTON_OFFSET = 16; // matches the resting -16px corner poke-out of .edit-text-button

// position: fixed is resolved against the nearest ancestor that establishes a containing block for
// fixed descendants (transform / filter / perspective / will-change). The agenda calendar's session row
// uses transform: translate(0,0), so the button's fixed coords are relative to that ancestor; we measure
// the offset and subtract it (origin is the viewport when no such ancestor exists).
function findFixedContainingBlock(el: HTMLElement | null): HTMLElement | null {
	let parent = el?.parentElement ?? null;
	while (parent) {
		const style = window.getComputedStyle(parent);
		if (
			style.transform !== 'none' ||
			style.perspective !== 'none' ||
			(!!style.filter && style.filter !== 'none') ||
			(!!style.willChange && /transform|perspective|filter/.test(style.willChange))
		) {
			return parent;
		}
		parent = parent.parentElement;
	}
	return null;
}

// floatEditButton: keep the edit button in the field's DOM subtree (so every :hover affordance on the
// field / card / module is preserved natively, since :hover is descendant-based) but switch it to
// position: fixed so it escapes the overflow:hidden clipping of tight cards. Visibility and hide-on-edit
// stay CSS-driven (.editable-text:hover / .active / :focus) — we only own the fixed coordinates.
function useFloatEditButton(containerRef: React.RefObject<HTMLElement>, enabled: boolean) {
	const [hovered, setHovered] = useState(false);
	const [pos, setPos] = useState<{ top: number; left: number; } | null>(null);
	const originRef = useRef<HTMLElement | null>(null);

	const updatePos = useCallback(() => {
		const el = containerRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		// the editor preview scales content via transform: scale(). getBoundingClientRect is post-scale
		// (visual), but fixed coords live in the containing block's pre-scale space and get re-scaled on
		// render — so divide the visual deltas by the field's rendered scale to convert them back.
		const scale = el.offsetWidth ? rect.width / el.offsetWidth : 1;
		const origin = originRef.current?.getBoundingClientRect();
		setPos({
			top: (rect.top - (origin?.top ?? 0)) / scale - FLOAT_EDIT_BUTTON_OFFSET,
			left: (rect.right - (origin?.left ?? 0)) / scale - FLOAT_EDIT_BUTTON_OFFSET,
		});
	}, [containerRef]);

	useEffect(() => {
		if (!enabled || !hovered) return;
		updatePos(); // just run once on load
		const onReflow = throttle(updatePos, 50);
		window.addEventListener('scroll', onReflow, true);
		return () => {
			window.removeEventListener('scroll', onReflow, true);
		};
	}, [enabled, hovered, updatePos]);

	// the containing-block ancestor can't change during a single hover, so resolve it once on enter
	const onFieldEnter = useCallback(() => {
		originRef.current = findFixedContainingBlock(containerRef.current);
		setHovered(true);
		updatePos();
	}, [containerRef, updatePos]);
	const onFieldLeave = useCallback(() => setHovered(false), []);

	return { pos, onFieldEnter, onFieldLeave };
}

const EditableTextSlim: React.FC<Props> = (props) => {
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const {
		colorOverride,
		defaultColor,
		disabled,
		displayCountAtXCharacter = 0,
		elementClass = "",
		elementName,
		hideColors = false,
		hidePencil,
		maxCharacters,
		onBlur = () => null,
		onEditableClick,
		onEditableContainerClick = () => null,
		onFocus = () => null,
		onOverrideChange,
		onTextChange,
		placeholder,
		tagType = "h1",
		value,
		isMultiLine = true,
		plainTextOnly,
		sanitize,
		floatEditButton,
		emptyState,
	} = props;

	const isEditorV3 = useTypedSelector(selectEditorNavV3);

	const colorPalette = useGetColorOptionsFromColor(
		workingEvent?.settings.design.colors,
		workingEvent?.settings.design.color_theme || EPaletteModes.Light
	);

	const color = colorOverride?.split("-")[0] as MultiColorType | undefined; //removing the "-color" from the classname
	const [text, setText] = useState(value);
	const [editSectionColorsModalResetKey, setEditSectionColorsModalResetKey] = useState(0);
	const [colorsOpen, setColorsOpen] = useState(false);
	const [toolBarActive, setToolBarActive] = useState(false);

	const editorRef = useRef<HTMLDivElement | HTMLInputElement | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const floatBtn = useFloatEditButton(containerRef, !!floatEditButton);

	const getFormattedValue = useCallback((text: string) => {
		let _value = text;
		if (plainTextOnly) {
			_value = convertFromHtml(_value);
		}
		if (!isMultiLine) {
			_value = stripNewLinesFromString(_value);
		}
		if (sanitize) {
			_value = DOMPurify.sanitize(_value);
		}
		return _value;
	}, [isMultiLine, plainTextOnly, sanitize]);

	// keep in sync with parent
	useEffect(() => {
		setText(getFormattedValue(value));
	}, [getFormattedValue, value]);

	// Use Refcallback per https://github.com/lovasoa/react-contenteditable/issues/161#issuecomment-736053428
	const handleTextChange = useRefCallback((event: ContentEditableEvent) => {
		const value = getFormattedValue(event.target.value);

		setText(value);
		onTextChange?.(value);
	}, []);

	const handleKeyDown = useRefCallback((event: React.KeyboardEvent) => {
		const isEnterKey = event.key === 'Enter';

		if (!isMultiLine && isEnterKey) {
			event.preventDefault(); /* prevent multi-lining */
			return;
		}

		const isArrow = event.key === 'ArrowLeft'
			|| event.code === 'ArrowLeft'
			|| event.keyCode === 37
			|| event.key === 'ArrowRight'
			|| event.code === 'ArrowRight'
			|| event.keyCode === 39;

		const isBackspace = event.key === 'Backspace'
			|| event.code === 'Backspace'
			|| event.keyCode === 8;

		const greaterThanMaxCharacters = maxCharacters && convertFromHtml(text).length >= maxCharacters;

		if (greaterThanMaxCharacters && !isBackspace && !isArrow) {
			event.preventDefault();
		}
	});

	// Use Refcallback per https://github.com/lovasoa/react-contenteditable/issues/161#issuecomment-736053428
	const handleBlur = useRefCallback(() => {
		const _text = getFormattedValue(text);

		if (_text !== value) { // Only update if changed
			props.onChange(_text);
		}
		onBlur();
		//We need to give the onClick function time to fire otherwise the onBlur fires first and removes the toolbar before it has a chance to fire the onClick.
		setTimeout(() => setToolBarActive(false), 300);
	}, [text]);

	function handleColorChange(_: string, color: string) {
		if (!onOverrideChange) return;
		onOverrideChange({
			color
		});
	}

	const startingColor = useStartingColor(color, defaultColor);
	const currentColor = useCurrentColor(startingColor, colorPalette);

	function openColors() {
		setColorsOpen(true);
	}

	// only the fixed coordinates are owned here; opacity / pointer-events / hide-on-edit stay CSS-driven
	const floatStyle: React.CSSProperties | undefined = floatEditButton ? {
		position: 'fixed',
		top: floatBtn.pos?.top,
		left: floatBtn.pos?.left,
		right: 'auto',
		zIndex: 10,
	} : undefined;

	const editButton = (
		<button
			className="round edit-text-button"
			onClick={() => editorRef.current?.focus()}
			disabled={disabled}
			style={floatStyle}
		>
			<Icon name={isEditorV3 ? ICONS.EDIT_DUOTONE : ICONS.EDIT} size={12} color={COLORS.WHITE} />
		</button>
	);

	const isTextEmpty = useMemo(() => isRichTextEmpty(text), [text]);

	const emptyStateWrapperStyles = useMemo(() => {
		return isTextEmpty && emptyState?.wrapperStyles || {};
	}, [emptyState?.wrapperStyles, isTextEmpty]);

	const emptyStateContentStyles = useMemo(() => {
		return isTextEmpty && emptyState?.contentStyles || {};
	}, [emptyState?.contentStyles, isTextEmpty]);

	const emptyStateFrameStyles = useMemo(() => {
		return isTextEmpty && emptyState?.frameStyles || {};
	}, [emptyState?.frameStyles, isTextEmpty]);

	const showOutline = useMemo(() => {
		return emptyState?.showOutline && isTextEmpty;
	}, [emptyState?.showOutline, isTextEmpty]);

	return (
		<div
			ref={containerRef}
			style={{ ...props.style ?? {}, ...emptyStateWrapperStyles }}
			className={classNames(
				"editable-text slim-container",
				{ disabled },
				props.className,
				{
					active: toolBarActive,
					showOutline,
				}
			)}
			onClick={onEditableContainerClick}
			onMouseEnter={floatEditButton ? floatBtn.onFieldEnter : undefined}
			onMouseLeave={floatEditButton ? floatBtn.onFieldLeave : undefined}
		>
			<div className="slim-frame" style={emptyStateFrameStyles}>
				<ContentEditable
					innerRef={editorRef}
					html={text || ''}
					onBlur={handleBlur}
					onChange={handleTextChange}
					onKeyDown={handleKeyDown}
					onClick={onEditableClick}
					onFocus={() => {
						setToolBarActive(true);
						onFocus();
					}}
					tagName={tagType}
					className={classNames(elementClass, colorOverride)}
					placeholder={placeholder}
					style={emptyStateContentStyles}
				/>

				<OptionalComponent display={!hidePencil}>
					{editButton}
				</OptionalComponent>
			</div>

			{startingColor && !hideColors ? (
				<div className="toolbar slim">
					<button
						className={classNames("small round color-display")}
						style={{
							backgroundColor: currentColor ? parseColor(currentColor) : "",
						}}
						onClick={openColors}
					/>
				</div>
			) : null}

			<EditSectionColorsModal
				open={colorsOpen}
				onClose={() => {
					setColorsOpen(false);
					// reset everything in the modal after it closes
					// timeout to keep the close animation
					setTimeout(() => {
						setEditSectionColorsModalResetKey(prev => prev + 1);
					}, 100);
				}}
				onFinish={handleColorChange}
				title={elementName || EContentColorOverrideNames.title}
				initialSelectedColorVariable={colorOverride?.split("-")[0] || defaultColor}
				key={editSectionColorsModalResetKey}
			/>
			<AnimatePresence>
				<LazyMotion features={domAnimation}>
					<OptionalComponent display={!!maxCharacters && toolBarActive && convertFromHtml(text).length >= displayCountAtXCharacter}>
						<m.span
							className="max-character-count"
							initial={{ scale: 0, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							exit={{ scale: 0, opacity: 0 }}
							transition={{
								type: "spring",
								stiffness: 900,
								damping: 15,
								velocity: 1.1
							}}
						>{convertFromHtml(text).length}/{maxCharacters}</m.span>
					</OptionalComponent>
				</LazyMotion>
			</AnimatePresence>
		</div >
	);
};

export default EditableTextSlim;
